import { useEffect, useRef, useState, type CSSProperties } from 'react';
import {
  PLEDGES,
  availablePledges,
  dailyPledge,
  maxTerm,
  type PledgeId,
  type Profile,
} from '../../../campaign';
import { t, type MessageKey } from '../../../i18n';
import { itemName, itemText } from '../../../app/describe';
import { sounds } from '../../../app/sound';
import { formatScore } from '../ScoreBurst';
import { getScene, patchScene, subscribeScene } from '../../scene';
import { Modal } from '../Modal';
import { BrokerHand } from './Broker';
import { Emblem } from './Emblem';
import { ItemSheet } from './Item';
import { NightObject } from './Objects';
import { info, roman, today } from './runText';

/**
 * The hub (docs/LONG_NIGHT_ART_DIRECTION.md §11.1–§11.2): the counter seen from the customer's
 * side. Behind it, in the half-dark: his shelf, where your lost pawns stand as shards with your
 * names in his ink; the Ledger, a book; the Daily Ledger's dated ticket. In the light: the
 * Pledges lying on velvet, the one being considered under the lamp, and the Terms as a ribbon of
 * wax seals whose fine print grows with every Term above the first. On confirming, his hand
 * reaches down and takes the Pledge into the dark.
 */

export type StartOptions = {
  pledge: PledgeId;
  term: number;
  heirloom?: string;
  daily?: string;
  seed?: string;
};

const MAX_TERM = 8;

/**
 * Each Pledge's color (§11.2): it tints the smoke's body and the lamp, the velvet tray and the
 * velvet the Pledge lies on, so the room takes on the thing being considered. The first color is
 * the smoke's, the second the light's.
 */
const PLEDGE_VIBES: Readonly<Record<PledgeId, readonly [string, string]>> = {
  'pocket-watch': ['#6e5223', '#ffd27a'],
  'wedding-ring': ['#6a2a44', '#ffb8c8'],
  fiddle: ['#6a3216', '#ff9a52'],
  locket: ['#1f4a5e', '#a8dcff'],
  dice: ['#165a3e', '#9cffc4'],
  'ivory-set': ['#4a4236', '#fff0d0'],
  'glass-eye': ['#0f5260', '#7ff2ff'],
  iou: ['#2c2c54', '#e8e0c4'],
  'bare-box': ['#55391a', '#e6b878'],
  scale: ['#24346a', '#bcd0ff'],
  'shuffled-box': ['#721828', '#ff7a8c'],
  ash: ['#34313a', '#d6ccc4'],
};
/** A locked Pledge lends the room no color. */
const LOCKED_LIGHT = '#6a5a7a';

/** How colorful a "#rrggbb" color is, 0 (grey) to 1. */
function chromaOf(hex: string): number {
  const n = parseInt(hex.slice(1), 16);
  const c = [(n >> 16) & 255, (n >> 8) & 255, n & 255];
  return (Math.max(...c) - Math.min(...c)) / 255;
}

/** The hue of a "#rrggbb" color, in degrees. */
function hueOf(hex: string): number {
  const n = parseInt(hex.slice(1), 16);
  const [r, g, b] = [(n >> 16) & 255, (n >> 8) & 255, n & 255].map((c) => c / 255) as [
    number,
    number,
    number,
  ];
  const max = Math.max(r, g, b);
  const d = max - Math.min(r, g, b);
  if (d === 0) return 0;
  const h = max === r ? ((g - b) / d) % 6 : max === g ? (b - r) / d + 2 : (r - g) / d + 4;
  return (h * 60 + 360) % 360;
}
/** The pixel velvet is drawn in wine red (hue 330°): turned to the Pledge's own color. */
const VELVET_HUE = 330;

/** The width of a Pledge's place in the row (styles.css, .hub-row --slot), and how far a finger
    must swipe to move to the next one. */
const SLOT = 132;
const SWIPE_MIN = 36;

export function HubScene({
  profile,
  reduceMotion,
  onStart,
  onLedger,
}: {
  profile: Profile;
  reduceMotion: boolean;
  onStart: (ids: string[], opts: StartOptions) => void;
  /** Opens the Ledger book (the collection, and his). */
  onLedger: (page: 'collection' | 'his') => void;
}) {
  const l = profile.ledger!;
  const open = availablePledges(l);
  const [index, setIndex] = useState(() =>
    Math.max(0, PLEDGES.indexOf(open[open.length - 1] ?? 'pocket-watch')),
  );
  const pledge = PLEDGES[index]!;
  const unlocked = open.includes(pledge);
  const top = unlocked ? maxTerm(l, pledge) : 1;
  const [term, setTerm] = useState(top);
  const chosenTerm = Math.min(term, top);
  const [heirloom, setHeirloom] = useState('');
  const [seed, setSeed] = useState('');
  const [more, setMore] = useState(false);
  const [daily, setDaily] = useState(false);
  const [taking, setTaking] = useState(false);
  const needed = Math.min(6, profile.stable.length);
  const [chosen, setChosen] = useState<string[]>(() => profile.stable.slice(0, needed));
  const classicRun = !!profile.run && profile.run.phase !== 'won' && profile.run.phase !== 'lost';
  const ready = chosen.length === needed && !classicRun && unlocked;
  const date = today();

  const go = (i: number) => {
    const next = ((i % PLEDGES.length) + PLEDGES.length) % PLEDGES.length;
    setIndex(next);
    const id = PLEDGES[next]!;
    if (open.includes(id)) setTerm(maxTerm(l, id));
  };
  // A finger swipes the Pledges left and right: the row follows it, and on letting go settles on
  // the one nearest the lamp. A swipe is not a tap on the Pledge it started on.
  const [drag, setDrag] = useState<number | null>(null);
  const swipe = useRef<{ x: number; id: number; moved: boolean } | null>(null);
  const swiped = useRef(false);
  const settle = (dx: number) => {
    const steps = Math.abs(dx) < SWIPE_MIN ? 0 : Math.max(1, Math.round(Math.abs(dx) / SLOT));
    if (steps) go(index - Math.sign(dx) * steps);
  };
  const begin = (opts: StartOptions) => {
    if (reduceMotion) return onStart(chosen, opts);
    // His hand reaches down and takes what you pledged into the dark (Glide).
    setTaking(true);
    sounds.play('breath');
    setTimeout(() => onStart(chosen, opts), 1300);
  };
  const opts: StartOptions = {
    pledge,
    term: chosenTerm,
    ...(heirloom ? { heirloom } : {}),
    ...(seed.trim() ? { seed: seed.trim() } : {}),
  };
  const lost = profile.collection;

  // The room takes on the Pledge being considered: its color in the smoke and the lamp (the scene
  // cross-fades), held against the screen's own scene, and given back on leaving.
  const [smoke, light] = unlocked ? PLEDGE_VIBES[pledge] : [null, LOCKED_LIGHT];
  useEffect(() => {
    const apply = () => {
      const s = getScene();
      if (s.tint !== smoke || s.lampTint !== (unlocked ? light : null))
        patchScene({ tint: smoke, lampTint: unlocked ? light : null });
    };
    apply();
    return subscribeScene(apply);
  }, [smoke, light, unlocked]);
  useEffect(() => () => patchScene({ tint: null, lampTint: null }), []);
  const vibe = {
    '--pledge': light,
    '--velvet-turn': `${Math.round(((hueOf(light) - VELVET_HUE + 540) % 360) - 180)}deg`,
    // A greyish Pledge (Ash, the IOU) lies on greyish velvet.
    '--velvet-sat': Math.min(1.15, Math.max(0.2, chromaOf(light) * 2.2)).toFixed(2),
  } as CSSProperties;

  return (
    <div className={`hub ${unlocked ? '' : 'is-locked'}`} style={vibe}>
      {/* Behind the counter, in the half-dark. */}
      <div className="hub-back">
        <button
          type="button"
          className="hub-shelf"
          onClick={() => onLedger('his')}
          aria-label={t('hub.hisShelf', { n: lost.length })}
          disabled={lost.length === 0}
        >
          {lost.slice(0, 5).map((id) => (
            <span key={id} className="hub-shard">
              <Emblem name="the-unredeemed" size={26} />
              <span className="hub-shard-name">{profile.pawns[id]?.name.split(' ')[0] ?? id}</span>
            </span>
          ))}
          {lost.length > 5 && <span className="hub-more">+{lost.length - 5}</span>}
          {lost.length === 0 && <span className="hub-empty-shelf" />}
        </button>
        <span className="hub-back-objects">
          <button
            type="button"
            className="hub-book"
            onClick={() => onLedger('collection')}
            aria-label={t('night.collection')}
            title={t('night.collection')}
          >
            <Emblem name="fixture.black-ledger" size={36} />
          </button>
          {/* The bell on the counter. */}
          <button
            type="button"
            className="hub-bell"
            onClick={() => sounds.bell(0)}
            aria-label={t('hub.bell')}
            title={t('hub.bell')}
          >
            <Emblem name="fixture.shop-bell" size={32} />
          </button>
        </span>
        {l.unlocked.includes('daily') && (
          <button type="button" className="hub-ticket" onClick={() => setDaily(true)}>
            <span>{formatDay(date)}</span>
            <span>{t('night.termN', { n: roman(3) })}</span>
            <span>{itemName(`pledge.${dailyPledge(date)}`)}</span>
          </button>
        )}
      </div>

      {classicRun && <p className="ln-sheet-note">{t('night.classicRunActive')}</p>}

      {/* The Pledges on velvet: the one under the lamp is the one being considered. */}
      <section
        className="hub-pledges"
        aria-label={t('night.pledge')}
        onPointerDown={(e) => {
          if (e.pointerType === 'mouse' && e.button !== 0) return;
          swiped.current = false;
          swipe.current = { x: e.clientX, id: e.pointerId, moved: false };
        }}
        onPointerMove={(e) => {
          const s = swipe.current;
          if (!s || s.id !== e.pointerId) return;
          const dx = e.clientX - s.x;
          if (!s.moved && Math.abs(dx) < 8) return;
          if (!s.moved) {
            s.moved = true;
            e.currentTarget.setPointerCapture(e.pointerId);
          }
          setDrag(dx);
        }}
        onPointerUp={(e) => {
          const s = swipe.current;
          swipe.current = null;
          if (!s?.moved) return;
          swiped.current = true;
          setDrag(null);
          settle(e.clientX - s.x);
        }}
        onPointerCancel={() => {
          swipe.current = null;
          setDrag(null);
        }}
        onClickCapture={(e) => {
          if (!swiped.current) return;
          swiped.current = false;
          e.stopPropagation();
        }}
      >
        <div
          className={`hub-row ${drag !== null ? 'is-dragging' : ''}`}
          style={{ '--i': index, '--drag': `${drag ?? 0}px` } as CSSProperties}
          onKeyDown={(e) => {
            if (e.key === 'ArrowLeft') go(index - 1);
            if (e.key === 'ArrowRight') go(index + 1);
          }}
        >
          {PLEDGES.map((id, i) => {
            const isOpen = open.includes(id);
            return (
              <button
                key={id}
                type="button"
                className={`hub-pledge ${i === index ? 'is-lit' : ''} ${isOpen ? '' : 'is-locked'} ${
                  taking && i === index ? 'is-taken' : ''
                }`}
                aria-current={i === index}
                aria-label={isOpen ? itemName(`pledge.${id}`) : t('night.locked')}
                tabIndex={i === index ? 0 : -1}
                onClick={() => (i === index ? undefined : go(i))}
              >
                {isOpen ? (
                  <NightObject spec={{ kind: 'pledge', emblem: `pledge.${id}` }} size={104} />
                ) : (
                  <span className="hub-locked">
                    <Emblem name={`pledge.${id}`} size={78} tone="silhouette" />
                  </span>
                )}
              </button>
            );
          })}
        </div>
        <button
          type="button"
          className="hub-arrow is-left"
          onClick={() => go(index - 1)}
          aria-label={t('hub.prev')}
        />
        <button
          type="button"
          className="hub-arrow is-right"
          onClick={() => go(index + 1)}
          aria-label={t('hub.next')}
        />
        {taking && <BrokerHand pose="reach" at={0.5} to={0.48} still={reduceMotion} />}
      </section>
      <div className={`hub-pledge-text ${unlocked ? '' : 'is-locked'}`} aria-live="polite">
        <h2 className="hub-name">{unlocked ? itemName(`pledge.${pledge}`) : t('night.locked')}</h2>
        {unlocked ? (
          <p className="hub-desc">{itemText(`pledge.${pledge}`)}</p>
        ) : (
          <p className="hub-unlock">{t(`item.pledge.${pledge}.unlock` as MessageKey)}</p>
        )}
        {unlocked && (l.terms[pledge] ?? 0) > 0 && (
          <span className="hub-won" title={t('night.wonTerm', { n: roman(l.terms[pledge]!) })}>
            <span className="wax-seal is-small">{roman(l.terms[pledge]!)}</span>
            {t('night.wonTerm', { n: roman(l.terms[pledge]!) })}
          </span>
        )}
      </div>

      {/* The Terms: a ribbon of wax seals; the contract's fine print grows with each. */}
      {unlocked && top > 1 && (
        <section className="hub-terms">
          <div className="term-ribbon" role="radiogroup" aria-label={t('night.term')}>
            {Array.from({ length: MAX_TERM }, (_, i) => i + 1).map((n) => (
              <button
                key={n}
                type="button"
                role="radio"
                aria-checked={chosenTerm === n}
                disabled={n > top}
                className={`wax-seal ${chosenTerm === n ? 'is-pressed' : ''} ${n > top ? 'is-closed' : ''}`}
                onClick={() => {
                  setTerm(n);
                  sounds.play('thud');
                }}
              >
                {roman(n)}
              </button>
            ))}
          </div>
          {chosenTerm > 1 && (
            <ol className="fine-print">
              {Array.from({ length: chosenTerm - 1 }, (_, i) => i + 2).map((n) => (
                <li key={n}>{t(`night.term.${n}` as MessageKey)}</li>
              ))}
            </ol>
          )}
        </section>
      )}

      <button type="button" className="hub-more-btn" onClick={() => setMore(true)}>
        {t('night.options')} · <span>{t('night.optionsHint')}</span>
      </button>

      <div className="ln-dock">
        <button
          type="button"
          className="btn btn-primary btn-large ln-dock-main"
          disabled={!ready || taking}
          onClick={() => begin(opts)}
        >
          {t('night.newRun')}
        </button>
      </div>

      {more && (
        <Modal title={t('night.options')} onClose={() => setMore(false)} className="ln-sheet">
          {profile.stable.length > needed && (
            <>
              <h3 className="ln-eyebrow">{t('night.lineupChoose', { n: needed })}</h3>
              <div className="tag-shelf">
                {profile.stable.map((id) => (
                  <button
                    key={id}
                    type="button"
                    className={`name-tag ${chosen.includes(id) ? 'is-on' : ''}`}
                    aria-pressed={chosen.includes(id)}
                    onClick={() =>
                      setChosen((c) =>
                        c.includes(id)
                          ? c.filter((x) => x !== id)
                          : c.length < needed
                            ? [...c, id]
                            : c,
                      )
                    }
                  >
                    {profile.pawns[id]?.name}
                    {l.hallmarks[id] && <small>{itemName(l.hallmarks[id]!.id)}</small>}
                  </button>
                ))}
              </div>
            </>
          )}
          {l.heirlooms.length > 0 && (
            <>
              <h3 className="ln-eyebrow">{t('night.heirloom')}</h3>
              <div className="heirloom-shelf">
                <button
                  type="button"
                  className={`marker ${heirloom === '' ? 'is-on' : ''}`}
                  onClick={() => setHeirloom('')}
                >
                  {t('night.noHeirloom')}
                </button>
                {l.heirlooms.map((h) => (
                  <button
                    key={h.id}
                    type="button"
                    className={`marker ${heirloom === h.id ? 'is-on' : ''}`}
                    onClick={() => setHeirloom(h.id)}
                  >
                    <span>{h.name}</span>
                    <small>{formatScore(h.inscription.recordedScore)}</small>
                  </button>
                ))}
              </div>
            </>
          )}
          <label className="field">
            <span className="field-label">{t('night.seedField')}</span>
            <input
              type="text"
              value={seed}
              maxLength={64}
              placeholder={t('night.seedPlaceholder')}
              onChange={(e) => setSeed(e.target.value)}
            />
            <span className="ln-hint">{t('night.seedHint')}</span>
          </label>
        </Modal>
      )}
      {daily && (
        <ItemSheet
          item={info(
            t('hub.daily'),
            t('night.daily', { date: formatDay(date) }),
            [
              t('hub.dailyText', {
                pledge: itemName(`pledge.${dailyPledge(date)}`),
                term: roman(3),
              }),
              l.daily[date]
                ? t('night.dailyBest', {
                    night: String(l.daily[date]!.night),
                    best: formatScore(l.daily[date]!.bestChain),
                  })
                : '',
            ]
              .filter(Boolean)
              .join('\n\n'),
            'ledger',
            { kind: 'favor', emblem: `pledge.${dailyPledge(date)}` },
          )}
          actions={[
            {
              label: t('hub.dailyBegin'),
              primary: true,
              disabled: chosen.length !== needed || classicRun,
              onClick: () => {
                setDaily(false);
                begin({ pledge: dailyPledge(date), term: 3, daily: date });
              },
            },
          ]}
          onClose={() => setDaily(false)}
        />
      )}
    </div>
  );
}

/** "3 October": a date as the dated ticket prints it. */
function formatDay(iso: string): string {
  const d = new Date(`${iso}T12:00:00`);
  try {
    return d.toLocaleDateString(undefined, { day: 'numeric', month: 'long' });
  } catch {
    return iso;
  }
}
